import { Injectable } from '@angular/core';
import { FirebaseAnalytics } from '@capacitor-firebase/analytics';
import { isPlatform } from '@ionic/angular';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private isWeb = isPlatform('mobileweb') || isPlatform('desktop');
  private isInitialized = false;

  constructor() {
    // Don't initialize immediately, wait for app to be ready
  }

  async init() {
    try {
      // console.log('Platform detection - isWeb:', this.isWeb);
      
      if (!this.isWeb) {
        // console.log('Initializing Firebase Analytics for native platform');
        await FirebaseAnalytics.setEnabled({ enabled: true });
        this.isInitialized = true;
        // console.log('Firebase Analytics initialized successfully for native platform');
      } else {
        // console.log('Firebase Analytics not needed for web platform - using gtag');
        this.isInitialized = true;
      }
    } catch (error) {
      console.error('Error initializing Firebase Analytics:', error);
      this.isInitialized = false;
    }
  }

  // Track screen views
  async trackScreenView(screenName: string, screenClass?: string) {
    try {
      if (!this.isInitialized) {
        await this.init();
      }

      const params = {
        screen_name: screenName,
        screen_class: screenClass || screenName
      };

      if (this.isWeb) {
        // For web, use gtag
        if (typeof (window as any).gtag === 'function') {
          (window as any).gtag('event', 'screen_view', params);
          // console.log('Web Analytics - Screen View tracked:', screenName, params);
        } else {
          console.warn('gtag not available for web analytics');
        }
      } else {
        // For native apps
        await FirebaseAnalytics.setCurrentScreen({
          screenName,
          screenClassOverride: screenClass || screenName
        });
        
        await FirebaseAnalytics.logEvent({
          name: 'screen_view',
          params: params
        });
        // console.log('Native Analytics - Screen View tracked:', screenName, params);
      }
    } catch (error) {
      console.error('Error tracking screen view:', error);
    }
  }

  // Track custom events
  async logEvent(eventName: string, params: { [key: string]: any } = {}) {
    try {
      if (!this.isInitialized) {
        await this.init();
      }

      if (this.isWeb) {
        if (typeof (window as any).gtag === 'function') {
          (window as any).gtag('event', eventName, params);
          // console.log('Web Analytics - Event tracked:', eventName, params);
        } else {
          console.warn('gtag not available for web analytics');
        }
      } else {
        await FirebaseAnalytics.logEvent({
          name: eventName,
          params: params
        });
        // console.log('Native Analytics - Event tracked:', eventName, params);
      }
    } catch (error) {
      console.error('Error logging event:', error);
    }
  }

  // Set user properties
  async setUserProperty(key: string, value: string) {
    try {
      if (!this.isInitialized) {
        await this.init();
      }

      if (this.isWeb) {
        // For web, set user properties using gtag
        if (typeof (window as any).gtag === 'function') {
          (window as any).gtag('set', { [key]: value });
          // console.log('Web Analytics - User property set:', key, value);
        } else {
          console.warn('gtag not available for web analytics');
        }
      } else {
        await FirebaseAnalytics.setUserProperty({ 
          key: key,
          value: value 
        });
        // console.log('Native Analytics - User property set:', key, value);
      }
    } catch (error) {
      console.error('Error setting user property:', error);
    }
  }

  // Set user ID
  async setUserId(userId: string) {
    try {
      if (!this.isInitialized) {
        await this.init();
      }

      if (this.isWeb) {
        if (typeof (window as any).gtag === 'function') {
          (window as any).gtag('set', { 'user_id': userId });
          // console.log('Web Analytics - User ID set:', userId);
        } else {
          console.warn('gtag not available for web analytics');
        }
      } else {
        await FirebaseAnalytics.setUserId({ userId });
        // console.log('Native Analytics - User ID set:', userId);
      }
    } catch (error) {
      console.error('Error setting user ID:', error);
    }
  }

  // Track user login
  async trackLogin(method: string = 'email') {
    await this.logEvent('login', { method: method });
  }

  // Track user signup
  async trackSignUp(method: string = 'email') {
    await this.logEvent('sign_up', { method: method });
  }

  // Track page views (alternative to screen views)
  async trackPageView(pageTitle: string, pageLocation?: string) {
    if (this.isWeb) {
      if (typeof (window as any).gtag === 'function') {
        (window as any).gtag('config', 'G-FSL26MQ234', {
          page_title: pageTitle,
          page_location: pageLocation || window.location.href
        });
      }
    } else {
      await this.trackScreenView(pageTitle);
    }
  }

  // Check if analytics is ready
  isReady(): boolean {
    return this.isInitialized;
  }

  // Get platform info
  getPlatform(): string {
    return this.isWeb ? 'web' : 'native';
  }

  // Test analytics functionality
  // async testAnalytics() {
  //   try {
  //     console.log('Testing Analytics Service...');
  //     console.log('Platform:', this.getPlatform());
  //     console.log('Is Initialized:', this.isInitialized);
      
  //     if (!this.isInitialized) {
  //       await this.init();
  //     }
      
  //     // Test basic event tracking
  //     await this.logEvent('analytics_test', {
  //       test_type: 'basic_functionality',
  //       timestamp: new Date().toISOString(),
  //       platform: this.getPlatform()
  //     });
      
  //     // Test screen view tracking
  //     await this.trackScreenView('test_screen', 'TestScreen');
      
  //     console.log('Analytics test completed successfully');
  //     return true;
  //   } catch (error) {
  //     console.error('Analytics test failed:', error);
  //     return false;
  //   }
  // }
}
